import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { ImporterLogic, ImporterState, type ImporterLogicDeps } from './logic'
import { Button } from '~common/components/button'
import { Card } from '~common/components/card'
import { LoadingIndicator } from '~common/components/loading'
import {
    ImportProgress,
    isImporterRunning,
    type LanguageKey,
} from '~/features/email-importer/services/types'
import { DEFAULT_INVOICE_KEYWORDS } from '~common/invoice-processing/crude-classification'
import { useAppContext } from '~/app-context'
import {
    DEFAULT_SLOW_QUEUE_LIMIT,
    getImporterYearOptions,
} from '../../services/constants'
import { GmailAccountSelector } from '~/features/ui-components/gmail-account-selector'

export interface Props extends ImporterLogicDeps {}

// Language display names
const LANGUAGE_DISPLAY_NAMES: Record<LanguageKey, string> = {
    en: 'English',
    de: 'German',
    es: 'Spanish',
    fr: 'French',
    it: 'Italian',
    pt: 'Portuguese',
    nl: 'Dutch',
}

const MONTHS = [
    { value: '1', label: 'Jan' },
    { value: '2', label: 'Feb' },
    { value: '3', label: 'Mar' },
    { value: '4', label: 'Apr' },
    { value: '5', label: 'May' },
    { value: '6', label: 'Jun' },
    { value: '7', label: 'Jul' },
    { value: '8', label: 'Aug' },
    { value: '9', label: 'Sep' },
    { value: '10', label: 'Oct' },
    { value: '11', label: 'Nov' },
    { value: '12', label: 'Dec' },
]

const IMPORTER_YEAR_OPTIONS = getImporterYearOptions()

export const EmailImporter: React.FC<Props> = (props) => {
    const appContext = useAppContext()
    const { logic, state } = useLogic(ImporterLogic, {
        ...props,
        ...appContext,
    })
    // Determine if importer is running based on term/year status
    const currentProgress = state
    const isRunning = isImporterRunning(currentProgress as ImportProgress)

    // Check if user has no credits remaining
    const entitlement = appContext.services.cache.getKey('entitlement')
    const remainingCredits = entitlement
        ? entitlement.invoicesRemainingConsumable +
          entitlement.invoicesRemainingSubscription
        : 0
    const hasNoCredits = remainingCredits === 0
    const hasLowCredits = remainingCredits <= DEFAULT_SLOW_QUEUE_LIMIT

    const availableLanguages = Object.keys(
        DEFAULT_INVOICE_KEYWORDS,
    ) as LanguageKey[]

    return (
        <>
            <Card>
                <ImporterContainer>
                    {(hasNoCredits || hasLowCredits) && (
                        <NoCreditsOverlay>
                            <NoCreditsContent>
                                <NoCreditsTitle>
                                    {hasNoCredits
                                        ? '0 Invoices Remaining'
                                        : `Auto-Scan not possible\ndue low credits`}
                                </NoCreditsTitle>
                                <NoCreditsDescription>
                                    We left you some credits so you can can
                                    <br />
                                    still search and import individual emails.
                                </NoCreditsDescription>
                                <BuyCreditsButton
                                    label="Buy more credits"
                                    onClick={() => {
                                        appContext.services.globalEvents.emit({
                                            type: 'openModal',
                                            modalType: 'pricing',
                                            metadata: {
                                                offerId: 'no-credits',
                                            },
                                        })
                                    }}
                                    size="large"
                                />
                            </NoCreditsContent>
                        </NoCreditsOverlay>
                    )}
                    {state.loadState === 'running' ? (
                        <LoadingIndicator />
                    ) : (
                        <>
                            <TitleBox>
                                <Title>Scan Gmail for Invoices</Title>
                                <GmailAccountSelector />
                            </TitleBox>
                            {/* Status/Action Bar */}
                            <StatusActionBar>
                                <LeftSide>
                                    <DateLabel>Scan from</DateLabel>
                                    <DateGroupContainer>
                                        <YearSelect
                                            value={
                                                state.config.dateRange.fromYear
                                            }
                                            onChange={(e) =>
                                                logic.updateFromYear(
                                                    e.target.value,
                                                )
                                            }
                                            disabled={
                                                state.importerChangeState ===
                                                    'running' || isRunning
                                            }
                                        >
                                            {IMPORTER_YEAR_OPTIONS.map(
                                                (year) => (
                                                    <option
                                                        key={year}
                                                        value={year}
                                                    >
                                                        {year}
                                                    </option>
                                                ),
                                            )}
                                        </YearSelect>
                                        <MonthSelect
                                            value={state.config.dateRange.fromMonth.toString()}
                                            onChange={(e) =>
                                                logic.updateFromMonth(
                                                    e.target.value,
                                                )
                                            }
                                            disabled={
                                                state.importerChangeState ===
                                                    'running' || isRunning
                                            }
                                        >
                                            {MONTHS.map((month) => (
                                                <option
                                                    key={month.value}
                                                    value={month.value}
                                                >
                                                    {month.label}
                                                </option>
                                            ))}
                                        </MonthSelect>
                                    </DateGroupContainer>
                                    <FromToSeparator>to</FromToSeparator>
                                    <DateGroupContainer>
                                        <YearSelect
                                            value={
                                                state.config.dateRange.toYear
                                            }
                                            onChange={(e) =>
                                                logic.updateToYear(
                                                    e.target.value,
                                                )
                                            }
                                            disabled={
                                                state.importerChangeState ===
                                                    'running' || isRunning
                                            }
                                        >
                                            {IMPORTER_YEAR_OPTIONS.map(
                                                (year) => (
                                                    <option
                                                        key={year}
                                                        value={year}
                                                    >
                                                        {year}
                                                    </option>
                                                ),
                                            )}
                                        </YearSelect>
                                        <MonthSelect
                                            value={state.config.dateRange.toMonth.toString()}
                                            onChange={(e) =>
                                                logic.updateToMonth(
                                                    e.target.value,
                                                )
                                            }
                                            disabled={
                                                state.importerChangeState ===
                                                    'running' || isRunning
                                            }
                                        >
                                            {MONTHS.map((month) => (
                                                <option
                                                    key={month.value}
                                                    value={month.value}
                                                >
                                                    {month.label}
                                                </option>
                                            ))}
                                        </MonthSelect>
                                    </DateGroupContainer>
                                </LeftSide>

                                {isRunning ? (
                                    <Button
                                        onClick={() => logic.pause()}
                                        disabled={
                                            state.importerChangeState ===
                                            'running'
                                        }
                                        label="Pause"
                                    />
                                ) : (
                                    <Button
                                        onClick={logic.start}
                                        disabled={
                                            state.importerChangeState ===
                                            'running'
                                        }
                                        label="Start Scan"
                                    />
                                )}

                                {/* Stop button - only show when running or paused */}
                                {isRunning && (
                                    <Button
                                        onClick={() => logic.stop()}
                                        disabled={
                                            state.importerChangeState ===
                                            'running'
                                        }
                                        label="Stop"
                                    />
                                )}
                            </StatusActionBar>

                            {/* Search Terms Section */}
                            <SearchTermsContainer>
                                <SearchTermsHeader>
                                    <SearchTermsTitle>
                                        Search Terms
                                    </SearchTermsTitle>
                                    <SearchTermsCount>
                                        {
                                            state.textFieldContent
                                                .trim()
                                                .split('\n')
                                                .filter(
                                                    (line) =>
                                                        line.trim().length > 0,
                                                ).length
                                        }
                                    </SearchTermsCount>
                                </SearchTermsHeader>
                                <SearchTermsInstructions>
                                    Select languages to include their
                                    invoice-related keywords or <br />
                                    add custom terms in the text area below.
                                </SearchTermsInstructions>

                                {/* Language Selection */}
                                <LanguageSelectionContainer>
                                    <LanguageSelectionHeader>
                                        Languages you send/get invoices
                                    </LanguageSelectionHeader>
                                    <LanguageButtonsContainer>
                                        {availableLanguages?.map((language) => (
                                            <Button
                                                key={language}
                                                variant={
                                                    state.config.selectedLanguages.includes(
                                                        language,
                                                    )
                                                        ? 'primary'
                                                        : 'outline'
                                                }
                                                size="small"
                                                label={
                                                    LANGUAGE_DISPLAY_NAMES[
                                                        language
                                                    ]
                                                }
                                                onClick={() =>
                                                    logic.toggleLanguage(
                                                        language,
                                                    )
                                                }
                                                disabled={
                                                    state.importerChangeState ===
                                                        'running' ||
                                                    isRunning ||
                                                    state.status.paused
                                                }
                                            />
                                        ))}
                                    </LanguageButtonsContainer>
                                </LanguageSelectionContainer>

                                <SearchTermsTextArea
                                    value={state.textFieldContent}
                                    onBlur={(e) => logic.saveSearchTerms()}
                                    onChange={(e) =>
                                        logic.updateSearchTermsText(
                                            e.target.value,
                                        )
                                    }
                                    disabled={
                                        state.importerChangeState ===
                                            'running' ||
                                        isRunning ||
                                        state.status.paused
                                    }
                                    placeholder="Language keywords are automatically added above. Add custom search terms here (one per line)..."
                                    rows={4}
                                />
                            </SearchTermsContainer>
                        </>
                    )}
                </ImporterContainer>
            </Card>
        </>
    )
}

const ImporterContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
`

const LeftSide = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
`

const StatusActionBar = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: ${({ theme }) => theme.spacing[6]};
    padding: ${({ theme }) => theme.spacing[4]};
    box-sizing: border-box;
    background-color: ${({ theme }) => theme.colors.background.bg2};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    margin-bottom: ${({ theme }) => theme.spacing[4]};
`

const CreditsContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
`

const CreditsText = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    color: ${({ theme }) => theme.colors.text.primary};
`

const DateGroupContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
`

const DateLabel = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    color: ${({ theme }) => theme.colors.text.primary};
    white-space: nowrap;
`

const FromToSeparator = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    color: ${({ theme }) => theme.colors.text.primary};
    white-space: nowrap;
    padding: 0 ${({ theme }) => theme.spacing[2]};
    box-sizing: border-box;
`

const YearSelect = styled.select`
    /* prettier-ignore */
    padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) =>
        theme.spacing[3]};
    box-sizing: border-box;
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    border: 1px solid ${({ theme }) => theme.colors.border.primary};
    background-color: ${({ theme }) => theme.colors.background.bg1};
    color: ${({ theme }) => theme.colors.text.primary};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    min-width: 100px;

    &:disabled {
        background-color: ${({ theme }) => theme.colors.background.bg3};
        color: ${({ theme }) => theme.colors.text.tertiary};
        cursor: not-allowed;
        opacity: 0.6;
    }

    &:focus {
        outline: 2px solid ${({ theme }) => theme.colors.border.focus};
        outline-offset: 2px;
    }
`

const MonthSelect = styled.select`
    /* prettier-ignore */
    padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) =>
        theme.spacing[3]};
    box-sizing: border-box;
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    border: 1px solid ${({ theme }) => theme.colors.border.primary};
    background-color: ${({ theme }) => theme.colors.background.bg1};
    color: ${({ theme }) => theme.colors.text.primary};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    min-width: 80px;

    &:disabled {
        background-color: ${({ theme }) => theme.colors.background.bg3};
        color: ${({ theme }) => theme.colors.text.tertiary};
        cursor: not-allowed;
        opacity: 0.6;
    }

    &:focus {
        outline: 2px solid ${({ theme }) => theme.colors.border.focus};
        outline-offset: 2px;
    }
`

const SearchTermsContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    width: 100%;
    margin-top: ${({ theme }) => theme.spacing[6]};
    box-sizing: border-box;
    background-color: ${({ theme }) => theme.colors.background.bg1};
    border-radius: ${({ theme }) => theme.borderRadius.md};
`

const SearchTermsHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 20px;
    width: 100%;
    margin-bottom: ${({ theme }) => theme.spacing[4]};
`

const SearchTermsTitle = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text.primary};
`

const SearchTermsCount = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.greyScale[100]};
`

const SearchTermsInstructions = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    color: ${({ theme }) => theme.colors.greyScale[100]};
    margin-bottom: ${({ theme }) => theme.spacing[4]};
`

const LanguageSelectionContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: ${({ theme }) => theme.spacing[4]};
`

const LanguageSelectionHeader = styled.h3`
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    margin-bottom: ${({ theme }) => theme.spacing[2]};
    color: ${({ theme }) => theme.colors.text.primary};
`

const LanguageButtonsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing[2]};
`

const SearchTermsTextArea = styled.textarea`
    width: 100%;
    padding: ${({ theme }) => theme.spacing[3]};
    box-sizing: border-box;
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    border: 1px solid ${({ theme }) => theme.colors.border.primary};
    background-color: ${({ theme }) => theme.colors.background.bg1};
    color: ${({ theme }) => theme.colors.text.primary};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    resize: vertical;
    min-height: 120px;

    &:disabled {
        background-color: ${({ theme }) => theme.colors.background.bg3};
        color: ${({ theme }) => theme.colors.text.tertiary};
        cursor: not-allowed;
        opacity: 0.6;
    }

    &:focus {
        outline: 2px solid ${({ theme }) => theme.colors.border.focus};
        outline-offset: 2px;
    }

    &::placeholder {
        color: ${({ theme }) => theme.colors.text.tertiary};
    }
`

const NoCreditsOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ theme }) => theme.colors.background.bg1}80;
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    border-radius: ${({ theme }) => theme.borderRadius.md};
`

const NoCreditsContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing[6]};
    text-align: center;
    padding: ${({ theme }) => theme.spacing[8]};
    box-sizing: border-box;
`

const NoCreditsTitle = styled.h1`
    font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text.primary};
    margin: 0;
    white-space: pre-line;
`

const BuyCreditsButton = styled(Button)`
    min-width: 200px;
`

const NoCreditsDescription = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize['xl']};
    color: ${({ theme }) => theme.colors.greyScale[50]};
    margin-bottom: ${({ theme }) => theme.spacing[4]};
`

const Title = styled.h1`
    font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text.primary};
    margin: 0;
    text-align: left;
    width: 100%;
`

const TitleBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: ${({ theme }) => theme.spacing[4]};
`
